import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

const SavedEmails = () => {
  const [savedEmails, setSavedEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Silme modalı için state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  
  // Düzenleme modalı için state
  const [showEditModal, setShowEditModal] = useState(false);
  const [emailToEdit, setEmailToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    email: '',
    description: ''
  });
  
  // Ekleme modalı için state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addFormData, setAddFormData] = useState({
    email: '',
    description: ''
  });

  // E-mail adreslerini getir ve otomatik kaydet
  const fetchSavedEmails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      // Önce tüm kaydedilmiş e-mail adreslerini getir
      const { data } = await axios.get('/api/saved-emails', config);
      
      if (data.success) {
        setSavedEmails(data.savedEmails);
      }
      
      // Sonra hatırlatmalardaki e-mail adreslerini otomatik kaydet
      // Hata olsa bile listeyi göstermeye devam et
      try {
        await axios.post('/api/saved-emails/auto-save', {}, config);
        // Otomatik kaydetme başarılı olursa, listeyi yenile
        const refreshData = await axios.get('/api/saved-emails', config);
        if (refreshData.data.success) {
          setSavedEmails(refreshData.data.savedEmails);
        }
      } catch (autoSaveError) {
        console.error('Otomatik kaydetme hatası:', autoSaveError);
        // Otomatik kaydetme hatası olsa bile kullanıcıya gösterme
        // Sadece konsola yaz
      }
    } catch (error) {
      setError('E-mail adresleri yüklenirken bir hata oluştu');
      toast.error('E-mail adresleri yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedEmails();
  }, []);
  
  // Sayfa yüklendiğinde filtre alanına focus ol
  useEffect(() => {
    if (!loading && !error) {
      setTimeout(() => {
        filterInputRef.current?.focus();
      }, 200);
    }
  }, [loading, error]);

  // Silme modalını aç
  const handleShowDeleteModal = (email) => {
    setEmailToDelete(email);
    setShowDeleteModal(true);
  };

  // Silme modalını kapat
  const handleCloseDeleteModal = () => {
    setEmailToDelete(null);
    setShowDeleteModal(false);
  };

  // E-mail adresini sil
  const handleDeleteEmail = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      const { data } = await axios.delete(`/api/saved-emails/${emailToDelete._id}`, config);
      
      if (data.success) {
        toast.success('E-mail adresi başarıyla silindi');
        setSavedEmails(savedEmails.filter(e => e._id !== emailToDelete._id));
        handleCloseDeleteModal();
        
        // E-mail adresi silindikten sonra filtre alanına focus ol
        setTimeout(() => {
          filterInputRef.current?.focus();
        }, 200);
      }
    } catch (error) {
      const message = error.response?.data?.message || 'E-mail adresi silinirken bir hata oluştu';
      toast.error(message);
    }
  };

  // Düzenleme modalını aç
  const handleShowEditModal = (email) => {
    setEmailToEdit(email);
    setEditFormData({
      email: email.email,
      description: email.description || ''
    });
    setShowEditModal(true);
  };
  
  // Modal açıldığında ilk alanı seç
  useEffect(() => {
    if (showEditModal) {
      setTimeout(() => {
        const firstInput = document.querySelector('#editEmailInput');
        if (firstInput) {
          firstInput.select();
        }
      }, 100);
    }
  }, [showEditModal]);

  // Düzenleme modalını kapat
  const handleCloseEditModal = () => {
    setEmailToEdit(null);
    setShowEditModal(false);
    
    // Modal kapandığında filtre alanına focus ol
    setTimeout(() => {
      filterInputRef.current?.focus();
    }, 200);
  };

  // Form değişikliklerini takip et
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };

  // E-mail adresini güncelle
  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      const { data } = await axios.put(`/api/saved-emails/${emailToEdit._id}`, editFormData, config);
      
      if (data.success) {
        toast.success('E-mail adresi başarıyla güncellendi');
        setSavedEmails(savedEmails.map(e => e._id === emailToEdit._id ? data.savedEmail : e));
        handleCloseEditModal();
      }
    } catch (error) {
      const message = error.response?.data?.message || 'E-mail adresi güncellenirken bir hata oluştu';
      toast.error(message);
    }
  };
  
  // Ekleme modalını aç
  const handleShowAddModal = () => {
    setAddFormData({
      email: '',
      description: ''
    });
    setShowAddModal(true);
  };
  
  // Modal açıldığında ilk alanı focus yap
  useEffect(() => {
    if (showAddModal) {
      setTimeout(() => {
        const firstInput = document.querySelector('#addEmailInput');
        if (firstInput) {
          firstInput.focus();
        }
      }, 100);
    }
  }, [showAddModal]);
  
  // Ekleme modalını kapat
  const handleCloseAddModal = () => {
    setShowAddModal(false);
    
    // Modal kapandığında filtre alanına focus ol
    setTimeout(() => {
      filterInputRef.current?.focus();
    }, 200);
  };
  
  // Form değişikliklerini takip et
  const handleAddFormChange = (e) => {
    const { name, value } = e.target;
    setAddFormData({
      ...addFormData,
      [name]: value
    });
  };
  
  // Yeni e-mail adresi ekle
  const handleAddEmail = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      const { data } = await axios.post('/api/saved-emails', addFormData, config);
      
      if (data.success) {
        toast.success('E-mail adresi başarıyla eklendi');
        setSavedEmails([...savedEmails, data.savedEmail]);
        handleCloseAddModal();
      }
    } catch (error) {
      const message = error.response?.data?.message || 'E-mail adresi eklenirken bir hata oluştu';
      toast.error(message);
    }
  };
  
  // Pagination için state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Filtreleme için state
  const [filterText, setFilterText] = useState('');
  const filterInputRef = useRef(null);
  
  // Sıralama için state
  const [sortField, setSortField] = useState('email');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Filtrelenmiş e-mail adresleri
  const filteredEmails = savedEmails.filter(email => {
    const searchText = filterText.toLowerCase();
    return (
      email.email.toLowerCase().includes(searchText) ||
      (email.description && email.description.toLowerCase().includes(searchText))
    );
  });
  
  // Sıralanmış e-mail adresleri
  const sortedEmails = [...filteredEmails].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    // String değerler için
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  
  // Pagination için hesaplamalar
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEmails = sortedEmails.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedEmails.length / itemsPerPage);
  
  // Sayfa değiştirme
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  // Sayfa başına gösterilecek öğe sayısını değiştirme
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Sayfa sayısı değiştiğinde ilk sayfaya dön
  };

  return (
    <div>
      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </Spinner>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Form.Group className="d-flex align-items-center" style={{ width: '300px' }}>
              <Form.Control
                ref={filterInputRef}
                type="text"
                placeholder="E-mail adresi veya açıklama ile ara..."
                value={filterText}
                onChange={(e) => {
                  setFilterText(e.target.value);
                  setCurrentPage(1); // Filtreleme yapıldığında ilk sayfaya dön
                }}
                autoComplete="off"
              />
              {filterText && (
                <Button 
                  variant="outline-secondary" 
                  className="ms-2"
                  onClick={() => {
                    setFilterText('');
                    setCurrentPage(1);
                    // Temizle butonuna tıklayınca filtre alanına focus ol
                    setTimeout(() => {
                      filterInputRef.current?.focus();
                    }, 0);
                  }}
                >
                  Temizle
                </Button>
              )}
            </Form.Group>
            
            <div>
              <Button 
                variant="success"
                onClick={handleShowAddModal}
              >
                Yeni E-mail Ekle
              </Button>
            </div>
          </div>
          
          <div className="d-flex justify-content-end mb-3">
            <Form.Group className="d-flex align-items-center">
              <Form.Label className="me-2 mb-0">Sayfa başına:</Form.Label>
              <Form.Select 
                value={itemsPerPage} 
                onChange={handleItemsPerPageChange}
                style={{ width: '80px' }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Form.Select>
            </Form.Group>
          </div>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th 
                  onClick={() => {
                    if (sortField === 'email') {
                      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('email');
                      setSortDirection('asc');
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  className="user-select-none"
                >
                  E-mail Adresi {sortField === 'email' && (
                    <span>{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th 
                  onClick={() => {
                    if (sortField === 'description') {
                      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('description');
                      setSortDirection('asc');
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  className="user-select-none"
                >
                  Açıklama {sortField === 'description' && (
                    <span>{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th 
                  onClick={() => {
                    if (sortField === 'createdAt') {
                      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('createdAt');
                      setSortDirection('desc');
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  className="user-select-none"
                >
                  Eklenme Tarihi {sortField === 'createdAt' && (
                    <span>{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {currentEmails.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-3">
                    {filterText ? 'Arama kriterlerine uygun e-mail adresi bulunamadı.' : 'Henüz kaydedilmiş e-mail adresi bulunmuyor.'}
                  </td>
                </tr>
              ) : (
                currentEmails.map(email => (
                  <tr key={email._id}>
                    <td>{email.email}</td>
                    <td>{email.description || '-'}</td>
                    <td>{new Date(email.createdAt).toLocaleString('tr-TR')}</td>
                    <td>
                      <Button 
                        variant="primary" 
                        size="sm" 
                        className="me-2"
                        onClick={() => handleShowEditModal(email)}
                      >
                        Düzenle
                      </Button>
                      <Button 
                        variant="danger" 
                        size="sm"
                        onClick={() => handleShowDeleteModal(email)}
                      >
                        Sil
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          
          {/* Pagination */}
          {savedEmails.length > 0 && totalPages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <ul className="pagination">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Önceki
                  </button>
                </li>
                
                {[...Array(totalPages).keys()].map(number => (
                  <li 
                    key={number + 1} 
                    className={`page-item ${currentPage === number + 1 ? 'active' : ''}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(number + 1)}
                    >
                      {number + 1}
                    </button>
                  </li>
                ))}
                
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Sonraki
                  </button>
                </li>
              </ul>
            </div>
          )}
          
          <div className="text-center mt-2">
            <small className="text-muted">
              {savedEmails.length > 0 ? (
                `Toplam ${savedEmails.length} e-mail adresi, ${indexOfFirstItem + 1}-${Math.min(indexOfLastItem, sortedEmails.length)} arası gösteriliyor`
              ) : (
                'Henüz kaydedilmiş e-mail adresi bulunmuyor'
              )}
            </small>
          </div>
        </>
      )}
      
      {/* Silme Onay Modalı */}
      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
        <Modal.Header closeButton>
          <Modal.Title>E-mail Adresini Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{emailToDelete?.email}</strong> adresini silmek istediğinize emin misiniz?
          </p>
          <p className="text-danger">Bu işlem geri alınamaz!</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            İptal
          </Button>
          <Button variant="danger" onClick={handleDeleteEmail}>
            Sil
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Düzenleme Modalı */}
      <Modal show={showEditModal} onHide={handleCloseEditModal}>
        <Modal.Header closeButton>
          <Modal.Title>E-mail Adresini Düzenle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpdateEmail}>
            <Form.Group className="mb-3">
              <Form.Label>E-mail Adresi</Form.Label>
              <Form.Control
                id="editEmailInput"
                type="email"
                name="email"
                value={editFormData.email}
                onChange={handleEditFormChange}
                required
                autoFocus
                autoComplete="off"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Açıklama</Form.Label>
              <Form.Control
                type="text"
                name="description"
                value={editFormData.description}
                onChange={handleEditFormChange}
                autoComplete="off"
              />
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={handleCloseEditModal}>
                İptal
              </Button>
              <Button variant="primary" type="submit">
                Kaydet
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      
      {/* Ekleme Modalı */}
      <Modal show={showAddModal} onHide={handleCloseAddModal}>
        <Modal.Header closeButton>
          <Modal.Title>Yeni E-mail Adresi Ekle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddEmail}>
            <Form.Group className="mb-3">
              <Form.Label>E-mail Adresi</Form.Label>
              <Form.Control
                id="addEmailInput"
                type="email"
                name="email"
                value={addFormData.email}
                onChange={handleAddFormChange}
                required
                autoFocus
                autoComplete="off"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Açıklama</Form.Label>
              <Form.Control
                type="text"
                name="description"
                value={addFormData.description}
                onChange={handleAddFormChange}
                autoComplete="off"
              />
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={handleCloseAddModal}>
                İptal
              </Button>
              <Button variant="success" type="submit">
                Ekle
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SavedEmails;
